import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import {
  CreditCard,
  Plus,
  Eye,
  Pencil,
  Trash2,
  Check,
  Unlock,
  DollarSign,
} from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const LoanReceivedList = () => {
  const [loans, setLoans] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    search: '',
    lender_type: '',
    status: '',
    loan_type: '',
    overdue_only: false,
  });
  const [loanTypes, setLoanTypes] = useState([]);
  const [lenderTypes, setLenderTypes] = useState([]);
  const navigate = useNavigate();
  const perPage = 10;

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchData(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const fetchDropdownData = async () => {
    try {
      const [typesRes, lenderTypesRes] = await Promise.all([
        loanReceivedService.getLoanTypes(),
        loanReceivedService.getLenderTypes(),
      ]);
      setLoanTypes(typesRes.data || []);
      setLenderTypes(lenderTypesRes.data || []);
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  const fetchData = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = {};
      if (filterValues.status) params.status = filterValues.status;
      if (filterValues.lender_type) params.lender_type = filterValues.lender_type;
      if (filterValues.loan_type) params.loan_type = filterValues.loan_type;
      if (filterValues.search) params.search = filterValues.search;
      if (filterValues.overdue_only) params.overdue_only = 'true';

      const [loansRes, statsRes] = await Promise.all([
        loanReceivedService.getAll(page, perPage, params),
        loanReceivedService.getStatistics(filterValues.lender_type || null, filterValues.status || null),
      ]);

      setLoans(loansRes.data || []);
      setTotalPages(loansRes.pagination?.pages || 1);
      setStatistics(statsRes.data || null);
    } catch (err) {
      Alert.error('Failed to load loan records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterValues({
      search: '',
      lender_type: '',
      status: '',
      loan_type: '',
      overdue_only: false,
    });
    setCurrentPage(1);
  };

  const handleApprove = async (loanId, lenderName) => {
    const confirmed = await Alert.confirm(`Approve loan from "${lenderName}"?`, 'Approve Loan');
    if (confirmed) {
      try {
        await loanReceivedService.approve(loanId);
        Alert.success('Loan approved successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve loan');
      }
    }
  };

  const handleReject = async (loanId, lenderName) => {
    const reason = await Alert.prompt(
      `Enter rejection reason for loan from "${lenderName}":`,
      'Reject Loan'
    );
    if (reason !== null) {
      try {
        await loanReceivedService.reject(loanId, reason || 'No reason provided');
        Alert.success('Loan rejected successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject loan');
      }
    }
  };

  const handleActivate = async (loanId, lenderName) => {
    const confirmed = await Alert.confirm(`Activate loan from "${lenderName}"?`, 'Activate Loan');
    if (confirmed) {
      try {
        await loanReceivedService.activate(loanId);
        Alert.success('Loan activated successfully!');
        fetchData();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to activate loan');
      }
    }
  };

  const handleDelete = async (loanId, lenderName) => {
    const confirmed = await Alert.confirm(`Delete loan record from "${lenderName}"?`, 'Delete Loan');
    if (confirmed) {
      try {
        await loanReceivedService.delete(loanId);
        Alert.success('Loan deleted successfully!');
        if (loans.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchData();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete loan');
      }
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    { key: 'loan_id', label: 'Loan ID' },
    {
      key: 'lender',
      label: 'Lender',
      render: (loan) => (
        <div>
          <div className="font-medium">{loan.lender_name}</div>
          <div className="text-xs text-gray-500 capitalize">{loan.lender_type.replace('_', ' ')}</div>
        </div>
      ),
    },
    {
      key: 'loan_type',
      label: 'Type',
      render: (loan) => loan.loan_type.replace(/_/g, ' '),
    },
    {
      key: 'loan_amount',
      label: 'Amount',
      cellClassName: 'text-emerald-600 font-semibold',
      render: (loan) => formatCurrency(loan.loan_amount),
    },
    {
      key: 'amount_pending',
      label: 'Pending',
      cellClassName: 'font-semibold',
      render: (loan) => (
        <span className={loan.amount_pending > 0 ? 'text-red-600' : 'text-gray-600'}>
          {formatCurrency(loan.amount_pending)}
        </span>
      ),
    },
    {
      key: 'due_date',
      label: 'Due Date',
      render: (loan) => (
        <div>
          <div>{formatDate(loan.due_date)}</div>
          {loan.is_overdue && <span className="text-xs text-red-600 font-semibold">Overdue!</span>}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (loan) => <StatusBadge status={loan.status} />,
    },
  ];

  const getActions = (loan) => {
    const actions = [
      {
        label: 'View Details',
        icon: Eye,
        onClick: () => navigate(`/loans-received/view/${loan.loan_id}`),
      },
    ];

    if (loan.status === 'pending') {
      actions.push(
        { label: 'Approve', icon: Check, onClick: () => handleApprove(loan.loan_id, loan.lender_name) },
        { label: 'Edit', icon: Pencil, onClick: () => navigate(`/loans-received/edit/${loan.loan_id}`) },
        { label: 'Delete', icon: Trash2, variant: 'danger', onClick: () => handleDelete(loan.loan_id, loan.lender_name) }
      );
    }

    if (loan.status === 'approved') {
      actions.push(
        { label: 'Activate', icon: Unlock, onClick: () => handleActivate(loan.loan_id, loan.lender_name) },
        { label: 'Add Repayment', icon: DollarSign, onClick: () => navigate(`/loans-received/repayment/${loan.loan_id}`) }
      );
    }

    if (loan.status === 'active') {
      actions.push({
        label: 'Add Repayment',
        icon: DollarSign,
        onClick: () => navigate(`/loans-received/repayment/${loan.loan_id}`),
      });
    }

    return actions;
  };

  return (
    <PageLayout
      title="Loans Received"
      icon={CreditCard}
      actions={
        <Button icon={Plus} onClick={() => navigate('/loans-received/add')}>
          Add Loan
        </Button>
      }
    >
      {statistics && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-4">
          <div className="bg-white p-3 sm:p-4 rounded-card shadow-card border-l-4 border-brand">
            <p className="text-xs text-gray-600 mb-1">Total Loans</p>
            <p className="text-lg sm:text-2xl font-bold text-brand">{statistics.total_loans}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-card shadow-card border-l-4 border-green-500">
            <p className="text-xs text-gray-600 mb-1">Received</p>
            <p className="text-sm sm:text-base font-bold text-green-600">{formatCurrency(statistics.total_amount_received)}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-card shadow-card border-l-4 border-brand">
            <p className="text-xs text-gray-600 mb-1">Paid</p>
            <p className="text-sm sm:text-base font-bold text-blue-600">{formatCurrency(statistics.total_amount_paid)}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-card shadow-card border-l-4 border-red-500">
            <p className="text-xs text-gray-600 mb-1">Pending</p>
            <p className="text-sm sm:text-base font-bold text-red-600">{formatCurrency(statistics.total_amount_pending)}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-card shadow-card border-l-4 border-yellow-500">
            <p className="text-xs text-gray-600 mb-1">Active</p>
            <p className="text-lg sm:text-2xl font-bold text-yellow-600">{statistics.active_count}</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-card shadow-card border-l-4 border-purple-500">
            <p className="text-xs text-gray-600 mb-1">Overdue</p>
            <p className="text-lg sm:text-2xl font-bold text-purple-600">{statistics.overdue_count}</p>
          </div>
        </div>
      )}

      <FilterPanel
        filters={[
          { key: 'search', label: 'Search', placeholder: 'Search by name, ID...' },
          {
            key: 'lender_type',
            label: 'Lender Type',
            type: 'select',
            options: lenderTypes.map((type) => ({ value: type.value, label: type.label })),
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'active', label: 'Active' },
              { value: 'completed', label: 'Completed' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'defaulted', label: 'Defaulted' },
            ],
          },
          {
            key: 'loan_type',
            label: 'Loan Type',
            type: 'select',
            options: loanTypes.map((type) => ({ value: type.value, label: type.label })),
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={clearFilters}
      />

      <label className="flex items-center gap-2 text-sm mb-4 px-1">
        <input
          type="checkbox"
          checked={filterValues.overdue_only}
          onChange={(e) => setFilterValues((prev) => ({ ...prev, overdue_only: e.target.checked }))}
          className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
        />
        <span className="text-gray-700">Overdue Only</span>
      </label>

      <DataTable
        columns={columns}
        data={loans}
        loading={loading}
        emptyMessage="No loans found"
        rowKey="loan_id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default LoanReceivedList;
